import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messagesWrapper"]

  connect() {
    this._boundVisibility = this.handleVisibilityChange.bind(this)
    this._initialScrollDone = false
    this._nearBottomThresholdPx = 160

    // Turbo 8 n’expose pas `turbo:after-stream-render` : on enveloppe le render du stream.
    this._onBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this._onBeforeStreamRender)

    this.element.addEventListener("turbo:frame-load", this._onFrameLoad = (event) => {
      // Only react to the main conversation frame (direct child of this element).
      // Ignore nested lazy frames (timestamps, etc.) whose loading would
      // otherwise scroll the view back to the bottom.
      if (event.target.parentElement !== this.element) return

      this._initialScrollDone = false
      this.scrollToBottomReliably()
    })

    this.setupMutationObserver()

    this._onTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    document.addEventListener("turbo:submit-end", this._onTurboSubmitEnd)

    document.addEventListener("visibilitychange", this._boundVisibility)
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this._onTurboSubmitEnd)
    document.removeEventListener("turbo:before-stream-render", this._onBeforeStreamRender)
    document.removeEventListener("visibilitychange", this._boundVisibility)
    if (this.mutationObserver) this.mutationObserver.disconnect()
  }

  get wrapper() {
    return this.hasMessagesWrapperTarget ? this.messagesWrapperTarget : null
  }

  scrollToBottom() {
    const w = this.wrapper
    if (!w) return
    w.scrollTop = w.scrollHeight
  }

  scrollToBottomReliably() {
    this.scrollToBottom()
    requestAnimationFrame(() => {
      this.scrollToBottom()
      setTimeout(() => {
        this.scrollToBottom()
        this._initialScrollDone = true
      }, 150)
    })
  }

  /** True if the user is near the bottom (new incoming messages should stay visible). */
  shouldStickToBottom() {
    const w = this.wrapper
    if (!w) return true
    const distanceFromBottom = w.scrollHeight - w.scrollTop - w.clientHeight
    return distanceFromBottom <= this._nearBottomThresholdPx
  }

  /**
   * Append vers #messages-list (broadcast ActionCable / autre).
   * Important : mesurer « près du bas » *avant* le render, sinon après append
   * scrollHeight augmente et shouldStickToBottom devient faux à tort.
   */
  handleBeforeStreamRender(event) {
    const stream = event.detail?.newStream
    if (!(stream instanceof Element) || stream.tagName !== "TURBO-STREAM") return
    if (!this.element.isConnected) return
    if (stream.getAttribute("action") !== "append") return

    const targetId = stream.getAttribute("target")
    if (targetId !== "messages-list") return
    if (!this.element.querySelector("#messages-list")) return

    const userWasFollowing = this.shouldStickToBottom()
    const originalRender = event.detail.render
    const self = this

    event.detail.render = async function (streamElement) {
      await originalRender(streamElement)
      if (!userWasFollowing) return
      requestAnimationFrame(() => {
        self.scrollToBottomReliably()
      })
    }
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList" || mutation.addedNodes.length === 0) continue

        if (!this._initialScrollDone) {
          this.scrollToBottomReliably()
          return
        }
      }
    })

    this.mutationObserver.observe(this.element, { childList: true, subtree: true })
  }

  handleTurboSubmitEnd(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement) || !form.classList.contains("message-form")) return
    if (!event.detail?.success) return
    if (!this.element.querySelector("#messages-list")) return

    requestAnimationFrame(() => {
      this.scrollToBottomReliably()
    })
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      this.scrollToBottom()
    }
  }

  scrollBottom() {
    this.scrollToBottom()
  }
}
